import { NavigationContainerRefWithCurrent, Route } from '@react-navigation/native';
import { useBottomSheet } from 'context/BottomSheetContext';
import { useGlobalConfig } from 'context/GlobalContext';
import { useModalConfig } from 'context/ModalContext';
import * as WebBrowser from 'expo-web-browser';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { AttachStep } from 'react-native-spotlight-tour';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { Platform } from 'utils/platform';

import bandBet from 'assets/navbar/bandbet.png';
import bandBetWhite from 'assets/navbar/bandbet_white.png';
import bandcom from 'assets/navbar/bandcom.png';
import bandcomWhite from 'assets/navbar/bandcom_white.png';
import bandplacar from 'assets/navbar/bandplacar.png';
import bandplacarWhite from 'assets/navbar/bandplacar_white.png';
import bandshop from 'assets/navbar/bandshop.png';
import bandshopWhite from 'assets/navbar/bandshop_white.png';
import newco from 'assets/navbar/newco.png';
import newcoWhite from 'assets/navbar/newco_white.png';

interface BandMenuItem {
  id: number;
  title: string;
  name: string;
  url: string;
  defaultIcon: ImageSourcePropType;
  selectedIcon: ImageSourcePropType;
}

const BottomMenu: React.FC<{ navigationRef?: NavigationContainerRefWithCurrent<any> }> = ({
  navigationRef,
}) => {
  const [selected, setSelected] = useState<number | undefined>(undefined);
  const { handleToggleSheet, visible, setCurrentUrl, setCurrentMenu } = useBottomSheet();
  const { toggleModal, isModalVisible } = useModalConfig();
  const analytics = useAnalytics();
  const { isKidState } = useGlobalConfig();
  const currentRoute: Route<string> | undefined = navigationRef?.current?.getCurrentRoute();

  const menuItems: BandMenuItem[] = [
    {
      id: 1,
      title: 'Bandbet',
      name: 'Bandbet',
      url: 'https://www.bandbet.com',
      defaultIcon: bandBet,
      selectedIcon: bandBetWhite,
    },
    {
      id: 2,
      title: 'Band.com.br',
      name: 'Band.com.br',
      url: 'https://www.band.com.br',
      defaultIcon: bandcom,
      selectedIcon: bandcomWhite,
    },
    {
      id: 3,
      title: 'Bandplacar',
      name: 'Bandplacar',
      url: 'https://www.band.com.br/band-placar',
      defaultIcon: bandplacar,
      selectedIcon: bandplacarWhite,
    },
    {
      id: 4,
      title: 'Bandshop',
      name: 'Bandshop',
      url: 'https://www.bandshop.com.br',
      defaultIcon: bandshop,
      selectedIcon: bandshopWhite,
    },
    {
      id: 5,
      title: 'Newco Play',
      name: 'Newco Play',
      url: 'https://newcoplay.com.br/home',
      defaultIcon: newco,
      selectedIcon: newcoWhite,
    },
  ];

  const isBottomMenuVisible = useMemo(() => {
    if (
      isKidState ||
      Platform.isTV ||
      currentRoute?.name === 'Login' ||
      currentRoute?.name === 'ForceUpdate'
    ) {
      return false;
    }

    const isMobilePlatform = Platform.isMobileWeb() || ['android', 'ios'].includes(Platform.OS);
    return isMobilePlatform;
  }, [isKidState, currentRoute]);

  useEffect(() => {
    if (!isModalVisible && !visible) setSelected(undefined);
  }, [visible, isModalVisible, isKidState]);

  const handleOpenUrl = async (item: BandMenuItem, index: number) => {
    const url = item.url;

    if (!url) {
      visible && handleToggleSheet();
      setSelected(selected === index ? undefined : index);
      return toggleModal();
    }

    if (Platform.OS === 'web' || item.name === 'Bandbet') {
      visible && handleToggleSheet();
      await WebBrowser.openBrowserAsync(url);
    } else {
      setCurrentMenu({ value: index, name: item.name, image: item.defaultIcon as string });
      isModalVisible && toggleModal();

      !visible && handleToggleSheet(currentRoute?.name !== 'Program');
      setSelected(index);
      setCurrentUrl(url);
    }
  };

  useEffect(() => {
    isBottomMenuVisible && analytics.triggerBottomMenuViewEvent();
  }, [isBottomMenuVisible]);

  const renderItem = (item: BandMenuItem, index: number) => (
    <Pressable
      onPress={() => {
        handleOpenUrl(item, index);
      }}
      style={styles.menuItem}>
      <Image
        source={selected === index ? item.defaultIcon : item.selectedIcon}
        style={styles.menuIcon}
        accessibilityLabel={`Ícone do item ${item.name}`}
      />
      <Text style={[styles.menuLabel, { color: selected === index ? '#888888' : '#fff' }]}>
        {item.name}
      </Text>
    </Pressable>
  );

  if (!isBottomMenuVisible) return null;

  return (
    <AttachStep
      index={1}
      children={
        <View
          style={[
            styles.bottomBar,
            {
              height: Platform.OS !== 'ios' ? 70 : 90,
            },
          ]}>
          <FlatList
            data={menuItems}
            keyExtractor={(item: BandMenuItem) => String(item.id)}
            renderItem={({ item, index }) => renderItem(item, index)}
            contentContainerStyle={{
              flex: 1,
              justifyContent: 'space-around',
              paddingHorizontal: 20,
            }}
            style={{
              paddingBottom: Platform.OS !== 'ios' ? 0 : 10,
            }}
            showsHorizontalScrollIndicator={false}
            scrollEnabled={false}
            horizontal
          />
        </View>
      }
    />
  );
};

export default React.memo(BottomMenu);

const styles = StyleSheet.create({
  menuItem: {
    paddingHorizontal: 8,
    alignItems: 'center',
    flexDirection: 'column',
  },
  menuIcon: {
    width: 26,
    height: 26,
    resizeMode: 'contain',
  },
  menuLabel: {
    textAlign: 'center',
    fontSize: 10,
    lineHeight: 16,
    fontWeight: '400',
    color: '#fff',
    marginTop: 5,
  },
  bottomBar: {
    zIndex: 10,
    marginTop: 'auto',
    backgroundColor: '#252836',
    borderTopWidth: 1,
    borderTopColor: '#A8B4FF80',
    alignItems: 'center',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-around',
  },
});
